import { ICase } from "../chatService";

interface MockData {
    [key: string]: ICase;
}

/**
 * Updates a mock case status without triggering a full re-render
 * @param caseId The case ID to update
 * @param status The new status value
 * @returns The updated case or undefined if not found
 */
export const updateMockCaseStatus = (caseId: string, status: string): ICase | undefined => {
    if (mockData[caseId]) {
        mockData[caseId].status.status = status;
        return mockData[caseId];
    }
    return undefined;
};

/**
 * the keys of the mockData object are the caseIds that are available as demo cases in the microfrontend
 */
export const mockData: MockData = {
    demo1: {
        caseId: "demo1",
        chats: [],
        status: {
            status: "open",
            hasUserChat: false,
            hasTechnicanChat: false
        }
    },
    demo2: {
        caseId: "demo2",
        chats: [
            {
                sessionId: "demo2-123",
                author: "customer",
                group: "customer",
                timestamp: new Date().toISOString(),
                messages: [
                    {
                        source: "bot",
                        message: "Hello, how can I help you?"
                    },
                    {
                        source: "user",
                        message: "I have a problem with my order."
                    }
                ]
            }
        ],
        status: {
            status: "open",
            hasUserChat: true,
            hasTechnicanChat: false
        }
    },
    demo3: {
        caseId: "demo3",
        chats: [
            {
                sessionId: "demo3-123",
                author: "John Smith",
                group: "User",
                timestamp: new Date().toISOString(),
                messages: [
                    {
                        source: "bot",
                        name: "AI Assistant",
                        message: "How can I assist you today?",
                        timestamp: new Date().toISOString()
                    },
                    {
                        source: "user",
                        name: "John Smith",
                        message: "I need help with my machine maintenance.",
                        timestamp: new Date(Date.now() - 60000).toISOString()
                    },
                    {
                        source: "bot",
                        name: "AI Assistant",
                        message:
                            "I'd be happy to help with your machine maintenance. Could you please specify which machine you're referring to and what kind of maintenance issue you're experiencing?",
                        timestamp: new Date(Date.now() - 55000).toISOString()
                    },
                    {
                        source: "user",
                        name: "John Smith",
                        message:
                            "It's a TruLaser 5030 fiber. The cutting head needs calibration, but I'm not sure how to do it properly.",
                        timestamp: new Date(Date.now() - 50000).toISOString()
                    },
                    {
                        source: "bot",
                        name: "AI Assistant",
                        message:
                            "For the TruLaser 5030 fiber cutting head calibration, you'll need to follow these steps:\n\n1. Make sure the machine is in standby mode\n2. Access the calibration menu through the control panel\n3. Select 'Cutting Head Calibration'\n4. Follow the on-screen instructions to position the cutting head\n5. Run the automated calibration sequence\n\nWould you like me to provide more detailed instructions for any of these steps?",
                        timestamp: new Date(Date.now() - 45000).toISOString()
                    },
                    {
                        source: "user",
                        name: "John Smith",
                        message:
                            "Yes, please explain step 4 in more detail. I'm having trouble positioning the cutting head correctly.",
                        timestamp: new Date(Date.now() - 40000).toISOString()
                    },
                    {
                        source: "bot",
                        name: "AI Assistant",
                        message:
                            "For step 4 - positioning the cutting head correctly:\n\n1. First, ensure all safety protocols are followed and wear appropriate PPE\n2. Use the manual control to move the cutting head to the designated calibration point (marked as 'P1' on most systems)\n3. The head should be approximately 10mm from the calibration plate\n4. Use the fine adjustment controls to align the head with the crosshairs shown on the display\n5. Once positioned, press 'Confirm Position' on the touch screen\n6. The system will then ask you to move to position 'P2' - follow the same procedure\n\nIs this clear, or would you need additional information?",
                        timestamp: new Date(Date.now() - 35000).toISOString()
                    }
                ]
            },
            {
                sessionId: "demo3-456",
                author: "Sarah Johnson",
                group: "User",
                timestamp: new Date(Date.now() - 3600000).toISOString(),
                messages: [
                    {
                        source: "user",
                        name: "Sarah Johnson",
                        message: "When is my next scheduled maintenance?",
                        timestamp: new Date(Date.now() - 3660000).toISOString()
                    },
                    {
                        source: "bot",
                        name: "AI Assistant",
                        message: "Your next scheduled maintenance is on October 15th. Would you like me to provide more details?",
                        timestamp: new Date(Date.now() - 3600000).toISOString()
                    },
                    {
                        source: "user",
                        name: "Sarah Johnson",
                        message: "Yes, please. What will be covered in the maintenance?",
                        timestamp: new Date(Date.now() - 3550000).toISOString()
                    },
                    {
                        source: "bot",
                        name: "AI Assistant",
                        message:
                            "The October 15th maintenance will cover:\n\n1. Complete system diagnostics\n2. Laser source inspection and calibration\n3. Replacement of all filters\n4. Lubrication of moving parts\n5. Optical path cleaning and alignment\n6. Software updates\n\nThe estimated duration is 4-5 hours. Do you want to confirm this appointment?",
                        timestamp: new Date(Date.now() - 3500000).toISOString()
                    },
                    {
                        source: "user",
                        name: "Sarah Johnson",
                        message: "That works for me. What time will the technician arrive?",
                        timestamp: new Date(Date.now() - 3450000).toISOString()
                    },
                    {
                        source: "bot",
                        name: "AI Assistant",
                        message:
                            "The technician is scheduled to arrive at 9:00 AM on October 15th. Please ensure that the machine is accessible and not in use by that time. Would you like to receive a reminder a day before?",
                        timestamp: new Date(Date.now() - 3400000).toISOString()
                    },
                    {
                        source: "user",
                        name: "Sarah Johnson",
                        message:
                            "Yes, a reminder would be helpful. Also, will I need to prepare anything before the technician arrives?",
                        timestamp: new Date(Date.now() - 3350000).toISOString()
                    },
                    {
                        source: "bot",
                        name: "AI Assistant",
                        message:
                            "I've set up a reminder for October 14th. As for preparation, please:\n\n1. Clear the area around the machine (at least 2 meters on all sides)\n2. Have your maintenance logbook ready for updating\n3. Ensure a staff member with machine knowledge is available\n4. Back up any critical production files\n5. Complete any jobs in progress by the end of the previous day\n\nIs there anything else you need to know?",
                        timestamp: new Date(Date.now() - 3300000).toISOString()
                    }
                ]
            },
            {
                sessionId: "demo3-789",
                author: "Michael Chen",
                group: "Technician",
                timestamp: new Date(Date.now() - 7200000).toISOString(),
                messages: [
                    {
                        source: "user",
                        name: "Michael Chen",
                        message:
                            "I'm investigating the error code E-5723 that was reported earlier today. Need some information on possible causes.",
                        timestamp: new Date(Date.now() - 7260000).toISOString()
                    },
                    {
                        source: "bot",
                        name: "AI Assistant",
                        message:
                            "Error code E-5723 on TruLaser systems typically indicates a laser source cooling issue. Common causes include:\n\n1. Coolant level too low\n2. Coolant temperature out of range\n3. Blockage in cooling circuit\n4. Pump malfunction\n5. Temperature sensor failure\n\nHave you checked the coolant system yet?",
                        timestamp: new Date(Date.now() - 7200000).toISOString()
                    },
                    {
                        source: "user",
                        name: "Michael Chen",
                        message:
                            "Yes, coolant levels are normal and no visible blockage. The temperature reading is showing 32°C which seems high. Is that outside the normal range?",
                        timestamp: new Date(Date.now() - 7140000).toISOString()
                    },
                    {
                        source: "bot",
                        name: "AI Assistant",
                        message:
                            "32°C is indeed above the normal operating range for the cooling system, which should be between 18-28°C. This likely explains the error. Possible actions to resolve this:\n\n1. Check if the external chiller is functioning properly\n2. Inspect the heat exchanger for dust accumulation\n3. Verify that the ambient temperature in the room isn't too high\n4. Check the coolant flow rate\n\nWhich of these would you like to try first?",
                        timestamp: new Date(Date.now() - 7080000).toISOString()
                    },
                    {
                        source: "user",
                        name: "Michael Chen",
                        message: "Let's check the external chiller first. Where can I find the maintenance logs for this system?",
                        timestamp: new Date(Date.now() - 7020000).toISOString()
                    },
                    {
                        source: "bot",
                        name: "AI Assistant",
                        message:
                            "The maintenance logs can be accessed through:\n\n1. The Service Documentation portal at service.trumpf.com using your technician credentials\n2. The machine's control system under 'Maintenance History'\n3. The physical logbook that should be stored near the machine\n\nFor the external chiller specifically, check if there's a separate control panel. Most chillers have their own diagnostic system. Look for any warning lights or error messages on the chiller display.",
                        timestamp: new Date(Date.now() - 6960000).toISOString()
                    },
                    {
                        source: "user",
                        name: "Michael Chen",
                        message:
                            "Found the issue. The chiller's filter is completely clogged. I'll replace it now. Do you know the part number for this filter model?",
                        timestamp: new Date(Date.now() - 6900000).toISOString()
                    },
                    {
                        source: "bot",
                        name: "AI Assistant",
                        message:
                            "For the standard chiller used with TruLaser 5030 fiber, the filter part number is 1234567. However, if this is the enhanced cooling package, you'll need part number 1234568 instead. You can verify which one you need by checking the label on the filter housing or the chiller model number. Would you like me to check the inventory system to see if we have these filters in stock?",
                        timestamp: new Date(Date.now() - 6840000).toISOString()
                    }
                ]
            }
        ],
        status: {
            status: "open",
            hasUserChat: true,
            hasTechnicanChat: true
        }
    }
};
